import type { SalesRepCheckReason } from 'types';

type ErrorWithData = {
  data: {
    errorCode: string;
    message: string;
    requestId: string;
  };
};

type ErrorWithErrors = {
  data: {
    errorCode: string;
    message: string;
    requestId: string;
    errors: string[];
  };
};

export function isErrorWithData(error: unknown): error is ErrorWithData {
  return (
    typeof error === 'object' && error != null && 'data' in error && typeof (error.data as any).message === 'string'
  );
}

// Fastify schema-validation text ("body/phoneNumber must match pattern...", "body must be object") is
// not user-facing; only business-rule messages are worth showing, everything else gets the fallback.
export function getUserFacingErrorMessage(error: unknown, fallback: string): string {
  return isErrorWithData(error) && !/^body\b/.test(error.data.message) ? error.data.message : fallback;
}

export function isErrorWithErrors(error: unknown): error is ErrorWithErrors {
  return (
    typeof error === 'object' &&
    error != null &&
    'data' in error &&
    typeof (error.data as any).message === 'string' &&
    typeof (error.data as any).errors === 'object'
  );
}

const isSalesRepCheckReason = (value: unknown): value is SalesRepCheckReason =>
  typeof value === 'object' &&
  value != null &&
  'code' in value &&
  typeof value.code === 'string' &&
  'message' in value &&
  typeof value.message === 'string' &&
  'resolution' in value &&
  typeof value.resolution === 'string';

/** Extracts the structured sales rep check reasons from a rejected contract send, if present. */
export function getSalesRepCheckReasons(error: unknown): SalesRepCheckReason[] | undefined {
  if (typeof error !== 'object' || error == null || !('data' in error)) return undefined;
  const { data } = error;
  if (typeof data !== 'object' || data == null || !('additionalContext' in data)) return undefined;
  const { additionalContext } = data;
  if (typeof additionalContext !== 'object' || additionalContext == null || !('reasons' in additionalContext)) {
    return undefined;
  }
  const { reasons } = additionalContext;
  if (!Array.isArray(reasons) || reasons.length === 0 || !reasons.every(isSalesRepCheckReason)) return undefined;
  return reasons;
}

/** Safely attempt to parse a filename from response headers. */
export const getFilenameFromHeaders = (headers: Headers, defaultFilename: string) => {
  try {
    const contentDisposition = headers.get('Content-Disposition');
    if (!contentDisposition) {
      return defaultFilename;
    }
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (!filenameMatch || filenameMatch.length < 2) {
      return defaultFilename;
    }
    return filenameMatch[1].replace(/['"]/g, '');
  } catch (error) {
    console.error('Error parsing filename from headers:', error);
    return defaultFilename;
  }
};

/** Forces the browser to save a blob already in memory by simulating a click on a link. */
export const saveFile = (blob: Blob, fileName: string) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.setAttribute('download', fileName);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
};

/** If you need to download a file programmatically,
 * this forces the browser to download by simulating a click on a link.
 * This is preferred over having RTK Query cache any download file.
 */
export const downloadFile = async (url: string, defaultFilename: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    saveFile(blob, getFilenameFromHeaders(response.headers, defaultFilename));
  } catch (error) {
    console.error('Error downloading file:', error);
    throw new Error('Failed to download file. Please try again later.');
  }
};
